import type {
  Settings as LayoutSettings,
  MenuDataItem,
} from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { Modal, message } from 'antd';
import { useEffect } from 'react';

// 配置 message 全局设置，限制同时显示的消息数量
message.config({
  maxCount: 1,
});

import { jwtDecode } from 'jwt-decode';
import { queryMyMenuTree } from '@/api/system/menu/service';
import { AvatarDropdown, AvatarName, Footer, SelectLang } from '@/components';
import TabsBar from '@/components/TabsBar';
import { convertToMenuData, extractPermissions } from '@/utils/menuConverter';
import {
  clearToken,
  getToken,
  getTokenRemainingTime,
  isTokenValid,
} from '@/utils/token';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * Token 过期检查组件
 * 在应用挂载后启动定时器，检查 token 是否过期
 */
const TokenExpiredChecker: React.FC = () => {
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    const checkTokenExpired = () => {
      const remainingTime = getTokenRemainingTime();

      // Token 已过期
      if (remainingTime <= 0) {
        clearToken();
        Modal.warning({
          title: '登录已过期',
          content: '您的登录已过期，请重新登录',
          okText: '确定',
          onOk: () => {
            history.push(loginPath);
          },
        });
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
        return;
      }

      // Token 即将过期（剩余 5 分钟）
      if (remainingTime > 0 && remainingTime <= 300) {
        Modal.warning({
          title: '登录即将过期',
          content: `您的登录将在 ${Math.floor(remainingTime / 60)} 分钟后过期，请及时保存数据`,
          okText: '确定',
        });
        // 只提示一次，清除定时器
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }
    };

    // 启动定时器，每 30 秒检查一次
    timer = setInterval(checkTokenExpired, 30000);

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, []);

  return null;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
/**
 * 自定义多页签栏（tabsLayout.hasCustomTabs 由 tabs-layout 插件识别）
 * 组件见 @/components/TabsBar，逻辑与租户端 poss_tenant_v2.0 同款
 */
export const getCustomTabs = () => TabsBar;

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  menuData?: MenuDataItem[];
  permissions?: string[];
}> {
  // 检查 token 是否有效
  const tokenValid = isTokenValid();
  let currentUser: any;
  let menuData: MenuDataItem[] = [];
  let permissions: string[] = [];

  if (tokenValid) {
    const token = getToken();
    if (token) {
      try {
        // 解析 JWT token 获取用户信息
        const tokenInfo: any = jwtDecode(token);
        console.log('Token 解析后的用户信息:', tokenInfo);
        // 映射 token 字段到 currentUser，确保 name 字段存在
        currentUser = {
          ...tokenInfo,
          name: tokenInfo.username || 'User',
          avatar: tokenInfo.avatar,
        };

        // 获取当前用户按角色授权过滤后的菜单数据（未被角色勾选的菜单不会返回）
        try {
          const menuResponse = await queryMyMenuTree('HQ');
          if (menuResponse?.data) {
            menuData = convertToMenuData(menuResponse.data);
            permissions = extractPermissions(menuResponse.data);
            console.log('动态菜单数据:', menuData);
            console.log('权限列表:', permissions);
          }
        } catch (menuError) {
          console.error('获取菜单失败:', menuError);
        }
      } catch (error) {
        console.error('Token 解析失败:', error);
        currentUser = { name: 'User' };
      }
    }
  }

  return {
    settings: defaultSettings as Partial<LayoutSettings>,
    currentUser,
    menuData,
    permissions,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有有效 token，重定向到 login
      if (!isTokenValid() && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 使用动态菜单数据
    menuDataRender: () => initialState?.menuData || [],
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <TokenExpiredChecker />
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
};
